import { Add, Description, Event, MonetizationOn } from "@mui/icons-material" // Adjusted icons
import { Box, Chip, Typography } from "@mui/material"
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from "@mui/x-data-grid"
import { StyledButton } from "components/Button/StyledButton"
import { HaufeLoader } from "components/HaufeLoader/HaufeLoader"
import { useHaufeLoading } from "contexts/HaufeLoadingContext"
import { SalesQuote } from "graphql/types" // Changed to SalesQuote
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AppRoutes } from "store/routes"

const SalesQuotes = () => {
  const navigate = useNavigate()
  const { salesQuotes } = useStoreState((state) => state) // Changed to salesQuotes
  const { fetchSalesQuotes } = useStoreActions((actions) => actions) // Changed to fetchSalesQuotes
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { haufeOperationType } = useHaufeLoading()

  useEffect(() => {
    const loadSalesQuotes = async () => {
      try {
        setLoading(true)
        setError(null)
        await fetchSalesQuotes({ fresh: false }) // Changed to fetchSalesQuotes
      } catch (err) {
        console.error("Error loading sales quotes:", err) // Changed message
        setError(
          "Fehler beim Laden der Angebote. Bitte versuchen Sie es später erneut."
        ) // Changed message
      } finally {
        setLoading(false)
      }
    }

    loadSalesQuotes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRetry = async () => {
    try {
      setLoading(true)
      setError(null)
      await fetchSalesQuotes({ fresh: true })
    } catch (err) {
      console.error("Error reloading sales quotes:", err)
      setError(
        "Fehler beim Laden der Angebote. Bitte versuchen Sie es später erneut."
      )
    } finally {
      setLoading(false)
    }
  }

  const columns: GridColDef<SalesQuote>[] = [
    // Changed to SalesQuote type
    {
      field: "id",
      headerName: "ID",
      flex: 0.7,
    },
    {
      field: "quoteNbr",
      headerName: "Quote No.",
      flex: 1,
      renderCell: (params: GridRenderCellParams<SalesQuote>) => (
        <Typography variant="body2">{params.row.quoteNbr || "N/A"}</Typography>
      ),
    },
    {
      field: "businessAccount",
      headerName: "Business Account",
      flex: 1.5,
      renderCell: (params: GridRenderCellParams<SalesQuote>) => (
        <Typography variant="body2">
          {params.row.businessAccount || "N/A"}
        </Typography>
      ),
    },
    {
      field: "contactContact",
      headerName: "Contact",
      flex: 1.5,
      renderCell: (params: GridRenderCellParams<SalesQuote>) => (
        <Typography variant="body2">
          {params.row.contactContact || "N/A"}
        </Typography>
      ),
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      renderCell: (params: GridRenderCellParams<SalesQuote>) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {params.row.date && (
            <>
              <Event fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">
                {new Date(params.row.date).toLocaleDateString()}
              </Typography>
            </>
          )}
        </Box>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      renderCell: (params: GridRenderCellParams<SalesQuote>) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {params.row.description && (
            <>
              <Description fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2" noWrap>
                {params.row.description}
              </Typography>
            </>
          )}
        </Box>
      ),
    },
    {
      field: "total",
      headerName: "Total",
      flex: 1,
      renderCell: (params: GridRenderCellParams<SalesQuote>) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {params.row.total !== null && params.row.total !== undefined && (
            <>
              <MonetizationOn fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">
                {params.row.total.toFixed(2)}
              </Typography>
            </>
          )}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params: GridRenderCellParams<SalesQuote>) => (
        <Chip
          label={params.row.status || "N/A"}
          color={
            params.row.status === "Draft"
              ? "info"
              : params.row.status === "Approved" // Example
                ? "success"
                : params.row.status === "Sent"
                  ? "primary"
                  : "default"
          }
          size="small"
        />
      ),
    },
    {
      field: "opportunityID",
      headerName: "Opportunity ID",
      flex: 1,
      renderCell: (params: GridRenderCellParams<SalesQuote>) => (
        <Typography variant="body2">
          {params.row.opportunityID || "N/A"}
        </Typography>
      ),
    },
    {
      field: "expirationDate",
      headerName: "Expires",
      flex: 1,
      renderCell: (params: GridRenderCellParams<SalesQuote>) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {params.row.expirationDate && (
            <>
              <Event fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">
                {new Date(params.row.expirationDate).toLocaleDateString()}
              </Typography>
            </>
          )}
        </Box>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      getActions: (params: GridRowParams<SalesQuote>) => [
        <StyledButton
          key={`action-${params.row.id}`}
          buttonType="custom"
          customIcon={<Add fontSize="small" />}
          onClick={() =>
            navigate(`${AppRoutes.SALES_QUOTES}/${params.row.quoteNbr}`)
          }
          size="small"
        />,
      ],
      flex: 0.7,
    },
  ]

  const handleRowClick = (params: GridRowParams<SalesQuote>) => {
    navigate(`${AppRoutes.SALES_QUOTES}/${params.row.quoteNbr}`)
  }

  if (loading) {
    return (
      <HaufeLoader
        message={haufeOperationType || "Lade Angebote aus dem Haufe-System..."}
      />
    )
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
        <StyledButton
          onClick={handleRetry}
          sx={{ mt: 2 }}
        >
          Erneut versuchen
        </StyledButton>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Sales Quotes
      </Typography>
      <DataGrid
        rows={salesQuotes}
        columns={columns}
        onRowClick={handleRowClick}
        autoHeight
        sx={{
          "& .MuiDataGrid-row:hover": {
            cursor: "pointer",
          },
        }}
      />
    </Box>
  )
}

export default SalesQuotes
